/**
 * WorkspaceSessionWizard
 *
 * Conditions-met wizard shown when the user selects "Configure Session."
 * Only asks about what isn't already set — steps are skipped when conditions
 * are satisfied.
 *
 * Steps (conditional):
 *   session       — name, timing mode, entry mode (always)
 *   participants  — who's in the session (always)
 *   items         — pick workspace items (always)
 *   toolConfig    — tool-specific params (dot voting dots, buy-a-feature budget, Pareto cut-off)
 *   criteria      — select/create criteria (only when tool requires user-defined criteria)
 *   criteriaRanges — the scale every criterion is scored on, 1–5 recommended (only when
 *                    the tool asks for criteria); saved in the session's tool settings
 *   values        — pre-enter item scores (skipped if all values already present)
 *
 * After creation:
 *   live + participant entry → shared /live board (+ join notifications)
 *   async with future opensAt → session overview (+ scheduled notifications)
 *   all other modes → session Run tab
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, CheckCircle, Loader2, AlertTriangle,
  Minus, Plus as PlusIcon, X, Clock, UserCheck, Users, Search,
  SlidersHorizontal, Table2, Lock,
} from 'lucide-react';
import { DateTimePicker } from './DateTimePicker';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useCriteriaStore } from '../../stores/prioritization/criteriaStore';
import { usePrioritizationTaskStore } from '../../stores/prioritization/prioritizationTaskStore';
import { useValueProposalStore } from '../../stores/prioritization/valueProposalStore';
import { isOfficialValue } from '../../lib/valueScope';
import { saveSessionValue } from '../../lib/sessionValueEntry';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { useSpaceStore } from '../../stores/prioritization/spaceStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { REGISTRY_TOOL_SPECS } from '../../generated/registryToolSpecs';
import { SESSION_TOOL_DESCRIPTOR_BY_ANY_ID, TOOL_REQUIRED_CRITERIA, sessionHasObviousOrLockedCriteria, toolAsksForCriteria } from '../../lib/sessionToolCatalog';
import { ToolSuitabilityDots } from './ToolSuitabilityDots';
import type { SessionIntent } from '../../types/prioritization/session';
import { legacyModesForRunMode, type SessionRunMode } from '../../types/prioritization/session';
import { getOutputTypeForSessionTool } from '../../lib/toolRegistry';
import { createSessionWithFeedback, type CreateSessionInput } from '../../lib/sessions/createSessionFlow';
import { CREATE_SESSION_BUTTON_LABEL } from '../../lib/sessions/vocabulary';
import { useToast } from '../ui/Toast';
import { fetchSessionDefaults, sessionDefaultSourceLabel, type SessionDefaults } from '../../lib/sessionDefaults';
import { DEFAULT_SCORING_SCALE, scoringScaleLabel, scoringScaleToolConfig, type ScoringScaleRange } from '../../lib/toolSettings';
import { useTrackStepLocks } from '../../hooks/useTrackStepLocks';
import { ScoringScaleChoice } from './session/ScoringScaleChoice';

import { useSystemUsers } from '../../hooks/useSystemUsers';
import type { ToolItem } from '../../contexts/toolItemsHooks';

type WizardStep = 'session' | 'participants' | 'items' | 'toolConfig' | 'criteria' | 'criteriaRanges' | 'values' | 'submitting' | 'done';

const STEP_LABELS: Record<string, string> = {
  session: 'Session',
  participants: 'Participants',
  items: 'Items',
  toolConfig: 'Configure',
  criteria: 'Criteria',
  criteriaRanges: 'Value Range',
  values: 'Values',
};

// Tools that need a config step in the wizard, and what they configure
const TOOL_CONFIG_STEP: Record<string, 'dot_voting' | 'buy_a_feature' | 'pareto_analysis'> = {
  dot_voting: 'dot_voting',
  buy_a_feature: 'buy_a_feature',
  pareto_analysis: 'pareto_analysis',
};

const PAIRWISE_ITEM_WARN_THRESHOLD = 15;

/** participant_live → shared Live Session board */
/** The run mode as the picker below names it. */
const RUN_MODE_WORDS: Record<SessionRunMode, string> = {
  facilitator_live: 'Facilitator live',
  participant_live: 'Participant live',
  open: 'Open window',
};

function isLiveParticipantSession(runMode: SessionRunMode): boolean {
  return runMode === 'participant_live';
}

function isScheduledOpenSession(runMode: SessionRunMode, opensAt: Date | undefined): boolean {
  if (runMode !== 'open' || !opensAt) return false;
  return opensAt.getTime() > Date.now();
}

interface Props {
  /** Tool name used to look up the registry spec (requiresCriteria, typicalPhase, etc.) */
  toolName: string;
  /** Workspace items available to import */
  availableItems: ToolItem[];
  /** Called when the user cancels the wizard */
  onCancel: () => void;
  /**
   * Optional hook when embedded in a tool (e.g. Configure Session modal).
   * Called before navigation; live + participant sessions still route to /live.
   */
  onComplete?: (sessionId: string, items: ToolItem[]) => void;
}

// Map typicalPhase to session intent
const PHASE_TO_INTENT: Record<string, string> = {
  ARRANGE: 'rank_priorities',
  GATHER: 'converge_values',
  PRIORITIZE: 'rank_priorities',
  ENGAGE: 'select_attributes',
};

export function WorkspaceSessionWizard({ toolName, availableItems, onCancel, onComplete }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const userId = useUserStore(state => state.userId);
  const currentUserName = useUserStore(state => state.userName);
  const createTask = usePrioritizationTaskStore(state => state.createTask);
  const openTask = usePrioritizationTaskStore(state => state.openTask);

  // Look up registry spec by display name (case-insensitive, punctuation-normalized),
  // falling back to session catalog aliases so names like "RICE Scoring" resolve to the
  // registry spec whose displayName is "RICE".
  const normalize = (s: string) => s.toLowerCase().replace(/[\s\-/]+/g, '');
  const registrySpec = useMemo(() => {
    const norm = normalize(toolName);
    // 1. Direct display-name match
    const byName = REGISTRY_TOOL_SPECS.find(s => normalize(s.displayName) === norm);
    if (byName) return byName;
    // 2. Alias lookup via session catalog (e.g. "RICE Scoring" → alias "rice-scoring" → compatibilityId "rice")
    const matchedKey = Object.keys(SESSION_TOOL_DESCRIPTOR_BY_ANY_ID).find(k => normalize(k) === norm);
    if (matchedKey) {
      const desc = SESSION_TOOL_DESCRIPTOR_BY_ANY_ID[matchedKey];
      return REGISTRY_TOOL_SPECS.find(s => s.toolId === desc.compatibilityId);
    }
    return undefined;
  }, [toolName]);

  const requiresCriteria = registrySpec?.compatibility.requiresCriteria ?? false;
  /**
   * When true, the tool owns its criteria (fixed formula/axes).
   * The wizard skips the generic criteria picker — the tool handles them internally.
   * E.g. Impact-Effort Matrix (Impact/Effort), RICE (R/I/C/E), WSJF, Cost of Delay.
   */
  const toolDefinesCriteria = registrySpec?.compatibility.toolDefinesCriteria ?? false;
  const typicalPhase = registrySpec?.typicalPhase ?? 'ARRANGE';
  const intent = PHASE_TO_INTENT[typicalPhase] ?? 'rank_priorities';
  const toolId = registrySpec?.toolId ?? '';
  const configKind = TOOL_CONFIG_STEP[toolId] ?? null;
  const isPairwise = toolId === 'pairwise_ranking';

  // ── Step state ──────────────────────────────────────────────────────────────
  const [step, setStep] = useState<WizardStep>('session');
  const [error, setError] = useState<string | null>(null);

  // Phase 1 — Session config
  const [sessionName, setSessionName] = useState(`${toolName} — ${new Date().toLocaleDateString()}`);
  // D02: single canonical run mode replaces the legacy timingMode × entryMode pair.
  const [runMode, setRunMode] = useState<SessionRunMode>('facilitator_live');
  const [opensAt, setOpensAt] = useState<Date | undefined>(undefined);
  const [closesAt, setClosesAt] = useState<Date | undefined>(undefined);
  const [allowViewOtherResponses, setAllowViewOtherResponses] = useState(false);
  const [allowResponseChanges, setAllowResponseChanges] = useState(true);
  const [requireAllResponses, setRequireAllResponses] = useState(false);

  // Phase 1 (optional) — Cycle / track context
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [selectedTrackId, setSelectedTrackId] = useState<string>('');

  // The chain's defaults for the chosen cycle or track (S-40, get_session_defaults):
  // the mode starts from them, and while sessions here may not override, the
  // picker is not drawn — the server would refuse any other choice.
  const [modeDefaults, setModeDefaults] = useState<SessionDefaults | null>(null);
  const modeHeld = modeDefaults?.allowSessionOverride.value === false;
  useEffect(() => {
    let cancelled = false;
    setModeDefaults(null);
    if (!selectedCycleId && !selectedTrackId) return;
    void fetchSessionDefaults({ cycleId: selectedCycleId || null, trackId: selectedTrackId || null }).then(({ defaults }) => {
      if (cancelled || !defaults) return;
      setModeDefaults(defaults);
      setRunMode(defaults.runMode.value);
    });
    return () => { cancelled = true; };
  }, [selectedCycleId, selectedTrackId]);

  // Derive legacy pair from runMode — used for task creation and backward-compat fields only.
  const { timingMode, entryMode } = legacyModesForRunMode(runMode);

  // Small space (personal or team): hide cycle/track attachment and enterprise wizard steps.
  // Derived synchronously from the current space so it updates when the user switches spaces.
  const currentSpaceType = useSpaceStore((s) => s.getCurrentSpace()?.spaceType ?? null);
  const workspaceType: 'personal' | 'team' | 'organizational' | null = currentSpaceType === 'personal' ? 'personal'
    : currentSpaceType === 'team' ? 'team'
    : currentSpaceType === 'org' ? 'organizational'
    : null;
  const isSmallSpace = workspaceType === 'personal' || workspaceType === 'team';

  const cycles = useDegapProcessStore(s => s.cycles);
  const allTracks = useTrackStore(s => s.tracks);
  // When a cycle is selected, only show tracks for that cycle; otherwise show all
  const availableTracks = useMemo(
    () => selectedCycleId ? allTracks.filter(t => t.cycleId === selectedCycleId) : allTracks,
    [selectedCycleId, allTracks]
  );

  // Phase 2 — Participants
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<Set<string>>(new Set());
  const [participantSearch, setParticipantSearch] = useState('');
  const { users, loading: usersLoading } = useSystemUsers();

  // Phase 3 — Items
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Phase 3 — Tool-config
  const [dotsPerParticipant, setDotsPerParticipant] = useState(5);
  const [budgetPercentage, setBudgetPercentage] = useState(40);
  // Pareto's cut-off asked as the share of value the vital few carry:
  // pareto_threshold = 1 − share (toolSettings default 0.2 → 80%).
  const [vitalFewShare, setVitalFewShare] = useState(80);

  // Phase 3 — Criteria
  const [selectedCriteriaIds, setSelectedCriteriaIds] = useState<Set<string>>(new Set());

  // Phase 4 — The scale every criterion is scored on. One scale for the session:
  // the scoring screen and the engine score every criterion on it. This step
  // used to draw min/max boxes per criterion and never save them.
  const [scoringScale, setScoringScale] = useState<ScoringScaleRange>(DEFAULT_SCORING_SCALE);
  // On a track that sets one range, the session scores on it: the server writes
  // it into the new session whatever is sent here (S-39, migration 1038).
  const { trackScale, scaleLocked: trackScaleLocked } = useTrackStepLocks(selectedTrackId || null);

  // Phase 5 — Item values (itemId → attributeKey → value string)
  const [wizardItemValues, setWizardItemValues] = useState<Record<string, Record<string, string>>>({});

  // Value proposal store — for checking / saving pre-entered scores
  const acceptedValues = useValueProposalStore(s => s.acceptedValues);
  const fetchAcceptedValues = useValueProposalStore(s => s.fetchFromSupabase);
  const currentRoleForValues = useUserStore(s => s.currentRole);
  const canUserForValues = useUserStore(s => s.can);

  // ── Derived (steps, skip logic) — defined after all store hooks ─────────────

  const steps: WizardStep[] = useMemo(() => {
    const base: WizardStep[] = ['session', 'participants', 'items'];
    if (configKind) base.push('toolConfig');
    // Only include criteria steps when this tool asks for criteria the facilitator
    // picks. Tools with fixed axes/categories (Eisenhower, MoSCoW, Dot Voting) or
    // their own built-in dimensions (RICE, WSJF, Cost of Delay, Impact/Effort)
    // skip them. Same predicate as the Event Designer (toolAsksForCriteria).
    if (toolAsksForCriteria(toolId)) {
      base.push('criteria');
      base.push('criteriaRanges');
      base.push('values');
    }
    return base;
  }, [configKind, toolId]);

  const currentIndex = steps.indexOf(step as Exclude<WizardStep, 'submitting' | 'done'>);

  // ── Participant helpers ──────────────────────────────────────────────────────
  const toggleParticipant = (id: string) => setSelectedParticipantIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const filteredAvailableUsers = useMemo(() => {
    const q = participantSearch.toLowerCase();
    return users.filter(u =>
      u.id !== userId && // exclude self (facilitator is always included)
      !selectedParticipantIds.has(u.id) &&
      (u.name?.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    );
  }, [users, userId, selectedParticipantIds, participantSearch]);

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  // ── Item selection helpers ───────────────────────────────────────────────────
  const toggleItem = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const selectAll = () => setSelectedIds(new Set(availableItems.map(i => i.id)));
  const clearAll = () => setSelectedIds(new Set());

  // ── Workspace criteria ───────────────────────────────────────────────────────
  const workspaceCriteria = useCriteriaStore(s => s.criteria);
  const fetchCriteria = useCriteriaStore(s => s.fetchFromSupabase);
  const criteriaLoading = useCriteriaStore(s => s.syncState.isLoading);

  // Criteria objects for the current wizard selection
  const selectedCriteriaObjects = useMemo(
    () => workspaceCriteria.filter(c => selectedCriteriaIds.has(c.id)),
    [workspaceCriteria, selectedCriteriaIds]
  );

  // True when every selected item already has an accepted value for every selected criterion
  const allValuesPresent = useMemo(() => {
    if (selectedIds.size === 0 || selectedCriteriaObjects.length === 0) return false;
    for (const itemId of selectedIds) {
      for (const criterion of selectedCriteriaObjects) {
        // Only the official value counts — another session's value is not this one's (V-3).
        const hasValue = acceptedValues.some(
          av => av.itemId === itemId && av.attributeKey === criterion.attributeKey && isOfficialValue(av)
        );
        if (!hasValue) return false;
      }
    }
    return true;
  }, [selectedIds, selectedCriteriaObjects, acceptedValues]);

  /** Returns true when a step should be auto-skipped because its conditions are already met */
  const shouldSkipStep = useCallback((s: WizardStep): boolean => {
    switch (s) {
      case 'participants':
        // Personal workspaces are single-user — no need to invite anyone. Skip automatically.
        return workspaceType === 'personal';
      case 'criteria':
        // Skip when the tool either owns its own dimensions (toolDefinesCriteria) OR
        // simply doesn't require user-defined criteria at all (e.g. Eisenhower, MoSCoW,
        // Dot Voting, Speed Boat — their axes/categories are fixed by the tool itself).
        return toolDefinesCriteria || !requiresCriteria;
      case 'criteriaRanges':
        // The scale matters even with no criteria picked: the tool then scores its
        // own standard criteria on it. Skip only when the tool asks for no criteria.
        return toolDefinesCriteria || !requiresCriteria;
      case 'values':
        // Skip if no criteria or no items, all values filled, or tool doesn't need criteria
        return selectedCriteriaIds.size === 0 || selectedIds.size === 0 || allValuesPresent || toolDefinesCriteria || !requiresCriteria;
      default:
        return false;
    }
  }, [workspaceType, toolDefinesCriteria, requiresCriteria, selectedCriteriaIds.size, selectedIds.size, allValuesPresent]);

  // True when we're on the last step, OR when every remaining step would be auto-skipped.
  // This ensures the button says "Create Session" (not "Next") and calls handleSubmit
  // directly when no more meaningful steps follow (e.g. toolDefinesCriteria tools skip
  // criteria/ranges/values, so Items is effectively the last real step).
  const isLastStep = useMemo(() => {
    if (currentIndex === steps.length - 1) return true;
    for (let i = currentIndex + 1; i < steps.length; i++) {
      if (!shouldSkipStep(steps[i])) return false;
    }
    return true;
  }, [currentIndex, steps, shouldSkipStep]);

  // Fetch criteria when the user reaches the criteria step
  useEffect(() => {
    if (step === 'criteria') fetchCriteria();
  }, [step, fetchCriteria]);

  // Auto-select tool-required criteria once workspace criteria are loaded.
  // Only fires when the set is still empty so manual changes aren't overridden.
  const requiredCriteriaKeys = TOOL_REQUIRED_CRITERIA[toolId] ?? [];
  useEffect(() => {
    if (step !== 'criteria' || criteriaLoading) return;
    if (requiredCriteriaKeys.length === 0) return;
    if (selectedCriteriaIds.size > 0) return; // User already made a selection — leave it alone
    const toSelect = workspaceCriteria.filter((c) =>
      requiredCriteriaKeys.some((k) => c.attributeKey.toLowerCase() === k.toLowerCase())
    );
    if (toSelect.length > 0) {
      setSelectedCriteriaIds(new Set(toSelect.map((c) => c.id)));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, criteriaLoading, workspaceCriteria.length]);

  const toggleCriterion = (id: string) => setSelectedCriteriaIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  // Fetch accepted values when the user reaches the values step (so we can check coverage)
  useEffect(() => {
    if (step === 'values' || step === 'criteriaRanges') fetchAcceptedValues();
  }, [step, fetchAcceptedValues]);

  // ── Navigation ───────────────────────────────────────────────────────────────
  const goBack = () => {
    const idx = steps.indexOf(step as Exclude<WizardStep, 'submitting' | 'done'>);
    if (idx > 0) {
      // Walk backwards, skipping steps whose conditions are already met
      let prev = idx - 1;
      while (prev > 0 && shouldSkipStep(steps[prev])) prev--;
      setStep(steps[prev]);
    }
  };

  const goNext = () => {
    const idx = steps.indexOf(step as Exclude<WizardStep, 'submitting' | 'done'>);
    if (idx < steps.length - 1) {
      // Walk forwards, skipping steps whose conditions are already met
      let next = idx + 1;
      while (next < steps.length - 1 && shouldSkipStep(steps[next])) next++;
      // If the next candidate should also be skipped, still advance — just to the last non-skipped
      setStep(steps[next]);
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!userId) {
      setError('You must be signed in to create a session.');
      return;
    }
    setStep('submitting');
    setError(null);

    try {
      const selectedItemIds = Array.from(selectedIds);
      const chosenCriteriaIds = Array.from(selectedCriteriaIds);

      // Build tool-specific config when applicable — saved under the engine's
      // keys (src/lib/toolSettings.ts), which is what the tool pages read.
      const resolvedToolConfig: Record<string, unknown> | undefined =
        configKind === 'dot_voting' ? { dots_per_participant: dotsPerParticipant } :
        configKind === 'buy_a_feature' ? { budget_percentage: budgetPercentage / 100 } :
        configKind === 'pareto_analysis'
          ? {
              ...(toolAsksForCriteria(toolId) ? scoringScaleToolConfig(trackScale ?? scoringScale) : {}),
              pareto_threshold: Math.round(100 - vitalFewShare) / 100,
            } :
        toolAsksForCriteria(toolId) ? scoringScaleToolConfig(trackScale ?? scoringScale) :
        undefined;

      // cycle_linked mode requires a trackId; use app_linked when only a cycle is selected
      const sessionContextMode = selectedTrackId ? 'cycle_linked' : 'app_linked';

      const createResult = await createSessionWithFeedback({
        name: sessionName.trim() || `${toolName} session`,
        facilitatorId: userId,
        createdBy: userId,
        sessionContextMode,
        intent: intent as CreateSessionInput['intent'],
        modeConfig: {
          runMode,
          timingMode,
          entryMode,
          ...(runMode === 'open' && opensAt ? { opensAt } : {}),
          ...(runMode === 'open' && closesAt ? { closesAt } : {}),
          ...(entryMode === 'participants' ? {
            allowViewOtherResponses,
            allowResponseChanges,
            requireAllResponses,
          } : {}),
        } as CreateSessionInput['modeConfig'],
        ...(toolId && { selectedToolId: toolId }),
        ...(resolvedToolConfig && { toolConfig: resolvedToolConfig }),
        ...(selectedItemIds.length > 0 && { itemIds: selectedItemIds }),
        ...(chosenCriteriaIds.length > 0 && { initialCriteriaIds: chosenCriteriaIds }),
        ...(selectedCycleId && { cycleId: selectedCycleId }),
        ...(selectedTrackId && { trackId: selectedTrackId }),
      }, { toast: showToast });
      const session = createResult.session;

      if (session?.id) {
        // Add non-facilitator participants to session_participants
        const participantsToAdd = [...selectedParticipantIds].filter(pid => pid !== userId);
        if (participantsToAdd.length > 0) {
          try {
            const { useSessionParticipantsStore } = await import('../../stores/prioritization/sessionParticipantsStore');
            const addParticipant = useSessionParticipantsStore.getState().addParticipant;
            await Promise.all(
              participantsToAdd.map(participantId =>
                addParticipant({ sessionId: session.id, userId: participantId, role: 'contributor', addedBy: userId })
              )
            );
          } catch (participantErr) {
            console.warn('[WorkspaceSessionWizard] Could not add participants:', participantErr);
          }
        }

        const isLiveParticipant = isLiveParticipantSession(runMode);
        const isScheduled = isScheduledOpenSession(runMode, opensAt);
        const chosenItems = availableItems.filter(i => selectedIds.has(i.id));
        const allParticipantIds = userId ? [userId, ...participantsToAdd] : [...participantsToAdd];

        // Tools with built-in dimensions (Impact/Effort, RICE, etc.) skip the criteria wizard —
        // mark criteria finalized so live close / run transitions do not block on DRAFT.
        if (session.state === 'DRAFT' && sessionHasObviousOrLockedCriteria(session)) {
          const fin = await useSessionStore.getState().transitionSessionState(
            session.id,
            'CRITERIA_FINALIZED',
            userId,
            { participantCount: Math.max(1, allParticipantIds.length) }
          );
          if (!fin.success) {
            console.warn('[WorkspaceSessionWizard] Auto-finalize criteria:', fin.error);
          }
        }

        // Create a task for every non-facilitator participant so it appears in their
        // My Tasks queue with the right call-to-action for this session mode.
        if (participantsToAdd.length > 0) {
          try {
            const taskDescription = isScheduled && opensAt
              ? `"${sessionName.trim() || toolName}" is scheduled for ${opensAt.toLocaleString()}. You'll be notified when it's time to participate.`
              : isLiveParticipant
                ? `You've been invited to a live ${toolName} session. Join now to participate.`
                : entryMode === 'participants'
                  ? `You've been invited to submit your ${toolName} rankings. Open the session to participate.`
                  : `You've been invited to a live ${toolName} session. Join now to participate.`;
            const task = createTask({
              name: isScheduled
                ? `${sessionName.trim() || toolName}: scheduled`
                : `${sessionName.trim() || toolName}: participate now`,
              description: taskDescription,
              intent: `Participate in ${toolName} session`,
              category: 'prioritization',
              mode: timingMode,
              facilitatorId: userId,
              participantIds: allParticipantIds,
              sessionId: session.id,
              createdBy: userId,
              ...(isScheduled && opensAt ? { opensAt } : {}),
              ...(toolId && { toolId: toolId as import('@/types/prioritization').PrioritizationToolId }),
              ...(selectedCycleId && { cycleId: selectedCycleId }),
            });
            openTask(task.id);
          } catch (taskErr) {
            console.warn('[WorkspaceSessionWizard] Could not create participant tasks:', taskErr);
          }
        }

        // Scheduled async: tell invitees now; they join when the window opens.
        if (isScheduled && opensAt && participantsToAdd.length > 0) {
          try {
            const { notifySessionScheduled } = await import('../../lib/notificationService');
            void notifySessionScheduled(
              session.id,
              sessionName.trim() || toolName,
              opensAt,
              participantsToAdd,
              userId,
            );
          } catch (notifyErr) {
            console.warn('[WorkspaceSessionWizard] Could not send scheduled notifications:', notifyErr);
          }
        }

        // Save wizard-entered values the one session-entry way (roadmap 1C):
        // a contribution for this session, plus this session's value when the
        // person may accept values. They never become official values here.
        const valueSavePromises: Promise<unknown>[] = [];
        for (const [itemId, attrMap] of Object.entries(wizardItemValues)) {
          for (const [attributeKey, rawVal] of Object.entries(attrMap)) {
            const trimmed = rawVal.trim();
            if (trimmed && userId) {
              valueSavePromises.push(
                saveSessionValue({
                  itemId,
                  attributeKey,
                  value: isNaN(Number(trimmed)) ? trimmed : Number(trimmed),
                  sessionId: session.id,
                  sessionName: session.name,
                  trackId: session.trackId,
                  userId,
                  userName: currentUserName,
                  role: currentRoleForValues,
                  mayAcceptForSession: canUserForValues('values.accept'),
                }).then(r => { if (r.error) console.warn('[WorkspaceSessionWizard] Could not save value:', r.error); })
              );
            }
          }
        }
        if (valueSavePromises.length > 0) {
          await Promise.allSettled(valueSavePromises);
        }

        setStep('done');
        // Brief pause so the user sees the success state, then route appropriately.
        setTimeout(async () => {
          let destination = `/prioritization/sessions/${session.id}?tab=run`;
          if (isScheduled) {
            destination = `/prioritization/sessions/${session.id}`;
          }

          if (isLiveParticipant) {
            try {
              const participantNames: Record<string, string> = {};
              for (const pid of allParticipantIds) {
                const u = users.find(u => u.id === pid);
                participantNames[pid] = u?.name ?? pid;
              }

              const canonicalToolId =
                registrySpec?.toolId ??
                SESSION_TOOL_DESCRIPTOR_BY_ANY_ID[toolName]?.compatibilityId ??
                'pairwise_ranking';

              const { getCurrentWorkspaceId } = await import('../../lib/supabaseSync');
              const workspaceId = (session as { workspaceId?: string }).workspaceId ?? getCurrentWorkspaceId() ?? '';

              const { useToolSessionStore } = await import('../../stores/prioritization/toolSessionStore');
              await useToolSessionStore.getState().createToolSession({
                sessionId: session.id,
                workspaceId,
                toolType: canonicalToolId,
                toolName,
                outputType: getOutputTypeForSessionTool(canonicalToolId),
                mode: 'sync',
                participantIds: allParticipantIds,
                participantNames,
                facilitatorId: userId ?? '',
                facilitatorName: currentUserName ?? '',
                items: chosenItems.map(i => ({
                  id: i.id,
                  name: (i as { title?: string; name?: string }).title ?? i.name ?? 'Item',
                })),
              });

              const nonFacilitatorIds = allParticipantIds.filter(pid => pid !== userId);
              if (nonFacilitatorIds.length > 0) {
                // Criteria-obvious tools were already finalized above; anything
                // still in DRAFT is not a room to page people into.
                const { ensureSessionJoinable } = await import('../../lib/sessionGoLive');
                const goLive = await ensureSessionJoinable(session.id, userId ?? undefined, {
                  participantCount: Math.max(1, allParticipantIds.length),
                });
                if (goLive.wentLive) {
                  const { notifySessionLiveNow } = await import('../../lib/notificationService');
                  void notifySessionLiveNow({
                    sessionId: session.id,
                    sessionName: sessionName.trim() || toolName,
                    recipientIds: nonFacilitatorIds,
                    cycleId: session.cycleId,
                    senderId: userId ?? undefined,
                    assumeLive: true,
                  });
                } else {
                  console.warn('[WorkspaceSessionWizard] session not announced as live:', goLive.error);
                }
              }

              destination = `/prioritization/sessions/${session.id}/live`;
            } catch (liveErr) {
              console.error('[WorkspaceSessionWizard] Failed to open live session:', liveErr);
            }
          }

          onComplete?.(session.id, chosenItems);
          navigate(destination);
        }, 800);
      } else {
        setError(createResult.error ?? 'Session creation failed. Please try again.');
        setStep(steps[steps.length - 1]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Session creation failed.');
      setStep(steps[steps.length - 1]);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (step === 'submitting') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <Loader2 className="h-10 w-10 text-primary-500 animate-spin" />
        <p className="text-sm text-[var(--text-sec)] font-medium">Creating session…</p>
      </div>
    );
  }

  if (step === 'done') {
    const doneHint = isLiveParticipantSession(runMode)
      ? 'Opening the live session…'
      : isScheduledOpenSession(runMode, opensAt)
        ? 'Session scheduled — opening overview…'
        : 'Taking you there now…';
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <CheckCircle className="h-12 w-12 text-[var(--green)]" />
        <p className="text-sm font-semibold text-[var(--text-pri)]">Session created!</p>
        <p className="text-xs text-[var(--text-ter)]">{doneHint}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
              i === currentIndex
                ? 'bg-primary-600 text-white'
                : i < currentIndex
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
            }`}>
              {i + 1}
            </div>
            <span className={`text-xs font-medium ${
              i === currentIndex ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'
            }`}>
              {STEP_LABELS[s] ?? s}
            </span>
            {i < steps.length - 1 && (
              <div className="h-px w-6 bg-[var(--hover-tint)]" />
            )}
          </div>
        ))}
      </div>

      {/* ── Phase 1: Session config ───────────────────────────────────────── */}
      {step === 'session' && (
        <div className="space-y-5">
          {/* Session name */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Session name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              type="text"
              value={sessionName}
              onChange={e => setSessionName(e.target.value)}
              placeholder={`${toolName} session`}
              className="w-full px-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>

          {/* Personal workspace: no timing/entry config needed — just you */}
          {workspaceType === 'personal' && (
            <div className="bg-[var(--surface)] border border-[var(--rule)] px-4 py-3">
              <p className="text-xs text-[var(--text-ter)]">
                This is a personal session — you'll rank the items yourself. No timing or entry configuration needed.
              </p>
            </div>
          )}

          {/* Run mode — team/org workspaces only (D02: single 3-option picker) */}
          {workspaceType !== 'personal' && (
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Session mode</label>
            <p className="text-xs text-[var(--text-ter)] mb-3">How will participants and the facilitator work together?</p>
            {modeDefaults && (
              <p className="text-xs text-[var(--text-sec)] mb-3" data-testid="wizard-inherited-mode">
                {modeHeld
                  ? `${RUN_MODE_WORDS[modeDefaults.runMode.value]}, set by ${sessionDefaultSourceLabel(modeDefaults.allowSessionOverride.source, modeDefaults.workflow?.name)}. Sessions here may not change it.`
                  : `Starts as ${RUN_MODE_WORDS[modeDefaults.runMode.value].toLowerCase()}, from ${sessionDefaultSourceLabel(modeDefaults.runMode.source, modeDefaults.workflow?.name)}.`}
              </p>
            )}
            {!modeHeld && (
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setRunMode('facilitator_live')}
                className={`flex flex-col gap-1.5  border-2 p-3 text-left transition-all ${
                  runMode === 'facilitator_live'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/20'
                    : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40'
                }`}
              >
                <div className={`flex h-8 w-8 items-center justify-center  ${
                  runMode === 'facilitator_live' ? 'bg-primary-100 dark:bg-primary-900/40' : 'bg-[var(--hover-tint)]'
                }`}>
                  <UserCheck className={`h-4 w-4 ${runMode === 'facilitator_live' ? 'text-primary-600 dark:text-primary-400' : 'text-[var(--text-ter)]'}`} />
                </div>
                <span className={`text-xs font-semibold ${runMode === 'facilitator_live' ? 'text-[var(--text-pri)]' : 'text-[var(--text-sec)]'}`}>
                  Facilitator live
                </span>
                <span className="text-xs text-[var(--text-ter)] leading-tight">Facilitator drives; enters all responses</span>
              </button>

              <button
                type="button"
                onClick={() => setRunMode('participant_live')}
                className={`flex flex-col gap-1.5  border-2 p-3 text-left transition-all ${
                  runMode === 'participant_live'
                    ? 'border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20'
                    : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40'
                }`}
              >
                <div className={`flex h-8 w-8 items-center justify-center  ${
                  runMode === 'participant_live' ? 'bg-[var(--green-dim)] dark:bg-[var(--green)]/40' : 'bg-[var(--hover-tint)]'
                }`}>
                  <Users className={`h-4 w-4 ${runMode === 'participant_live' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`} />
                </div>
                <span className={`text-xs font-semibold ${runMode === 'participant_live' ? 'text-[var(--text-pri)]' : 'text-[var(--text-sec)]'}`}>
                  Participant live
                </span>
                <span className="text-xs text-[var(--text-ter)] leading-tight">Everyone enters in real-time</span>
              </button>

              <button
                type="button"
                onClick={() => setRunMode('open')}
                className={`flex flex-col gap-1.5  border-2 p-3 text-left transition-all ${
                  runMode === 'open'
                    ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20'
                    : 'border-[var(--rule)] hover:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]/40'
                }`}
              >
                <div className={`flex h-8 w-8 items-center justify-center  ${
                  runMode === 'open' ? 'bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40' : 'bg-[var(--hover-tint)]'
                }`}>
                  <Clock className={`h-4 w-4 ${runMode === 'open' ? 'text-[var(--mustard)] dark:text-[var(--mustard)]' : 'text-[var(--text-ter)]'}`} />
                </div>
                <span className={`text-xs font-semibold ${runMode === 'open' ? 'text-[var(--text-pri)]' : 'text-[var(--text-sec)]'}`}>
                  Open window
                </span>
                <span className="text-xs text-[var(--text-ter)] leading-tight">Async; open between two times</span>
              </button>
            </div>
            )}
          </div>
          )}

          {/* Open window scheduling — only shown for 'open' run mode */}
          {workspaceType !== 'personal' && runMode === 'open' && (
            <div className="grid grid-cols-2 gap-4 p-4 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Opens At
                </label>
                <DateTimePicker
                  value={opensAt}
                  onChange={setOpensAt}
                  placeholder="Pick open date & time…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Closes At
                </label>
                <DateTimePicker
                  value={closesAt}
                  onChange={setClosesAt}
                  minDate={opensAt}
                  placeholder="Pick close date & time…"
                />
              </div>
            </div>
          )}

          {/* Participant entry options — open mode or participant_live */}
          {workspaceType !== 'personal' && (runMode === 'participant_live' || runMode === 'open') && (
            <div className="p-4 bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)] space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowViewOtherResponses}
                  onChange={e => setAllowViewOtherResponses(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-[var(--rule)] rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-[var(--text-sec)]">Allow viewing other responses</span>
                  <p className="text-xs text-[var(--text-ter)]">Participants can see each other's responses before submitting</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowResponseChanges}
                  onChange={e => setAllowResponseChanges(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-[var(--rule)] rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-[var(--text-sec)]">Allow response changes</span>
                  <p className="text-xs text-[var(--text-ter)]">Participants can modify their responses after submission</p>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={requireAllResponses}
                  onChange={e => setRequireAllResponses(e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-[var(--rule)] rounded focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-[var(--text-sec)]">Require all responses</span>
                  <p className="text-xs text-[var(--text-ter)]">All participants must respond before the session can proceed</p>
                </div>
              </label>
            </div>
          )}

          {/* Tool info note */}
          {registrySpec && (
            <div className="bg-[var(--surface)] border border-[var(--rule)] px-4 py-3 space-y-2">
              <p className="text-xs text-[var(--text-ter)]">
                <span className="font-medium text-[var(--text-sec)]">{toolName}</span>
                {' '}is a <span className="font-medium">{registrySpec.compatibility.family}</span> tool.
                {toolDefinesCriteria && (
                  <> The evaluation dimensions are built into the tool — no separate criteria setup needed.</>
                )}
                {requiresCriteria && !toolDefinesCriteria && (
                  <> You&apos;ll choose which criteria to use in a later step.</>
                )}
                {registrySpec.compatibility.minParticipants && registrySpec.compatibility.minParticipants > 1 && (
                  <> Collaborative: min {registrySpec.compatibility.minParticipants} participants.</>
                )}
              </p>
              {toolId && (
                <ToolSuitabilityDots
                  intent={intent as SessionIntent}
                  toolId={toolId}
                />
              )}
            </div>
          )}

          {/* Optional: Attach to cycle / track */}
          {!isSmallSpace && (cycles.length > 0 || allTracks.length > 0) && (
            <div className="border border-[var(--rule)] bg-[var(--surface)] px-4 py-4 space-y-3">
              <p className="text-xs font-medium text-[var(--text-sec)]">Attach to cycle / track <span className="text-[var(--text-ter)] font-normal">(optional)</span></p>
              {cycles.length > 0 && (
                <div>
                  <label className="block text-xs text-[var(--text-ter)] mb-1">Cycle</label>
                  <select
                    value={selectedCycleId}
                    onChange={e => {
                      setSelectedCycleId(e.target.value);
                      setSelectedTrackId(''); // reset track when cycle changes
                    }}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">— No cycle —</option>
                    {cycles.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {availableTracks.length > 0 && (
                <div>
                  <label className="block text-xs text-[var(--text-ter)] mb-1">Track</label>
                  <select
                    value={selectedTrackId}
                    onChange={e => setSelectedTrackId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">— No track —</option>
                    {availableTracks.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {selectedTrackId && (
                <p className="text-xs text-primary-600">
                  Session will inherit configuration from the selected Track.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Phase 2: Participants ─────────────────────────────────────────── */}
      {step === 'participants' && (
        <div className="space-y-4">
          {/* Team context banner */}
          {workspaceType === 'team' && (
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] px-4 py-3">
              <p className="text-xs font-semibold text-[var(--text-sec)] mb-0.5">Team session</p>
              <p className="text-xs text-[var(--text-sec)]">
                Add your teammates below. They'll each vote on the items — live or async — and the session aggregates everyone's ranking into a single priority list.
              </p>
            </div>
          )}

          {/* Zone 1 — Selected participants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[var(--text-sec)]">
                Participants ({selectedParticipantIds.size + 1})
              </span>
              {selectedParticipantIds.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedParticipantIds(new Set())}
                  className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="border border-[var(--rule)] divide-y divide-[var(--rule)] overflow-hidden">
              {/* Facilitator row — always shown, not removable */}
              <div className="flex items-center gap-3 px-3 py-2.5 bg-[var(--surface)]">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold shrink-0">
                  {getInitials(currentUserName ?? 'F')}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-pri)] truncate">
                    {currentUserName ?? 'You'}
                  </p>
                </div>
                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">Facilitator</span>
              </div>

              {/* Selected participants */}
              {[...selectedParticipantIds].map(pid => {
                const user = users.find(u => u.id === pid);
                const display = user?.name ?? user?.email ?? pid;
                return (
                  <div key={pid} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-semibold shrink-0">
                      {getInitials(display)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--text-pri)] truncate">{display}</p>
                      {user?.email && user.name && (
                        <p className="text-xs text-[var(--text-ter)] truncate">{user.email}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleParticipant(pid)}
                      className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })}

              {selectedParticipantIds.size === 0 && (
                <div className="px-3 py-3 text-xs text-[var(--text-ter)] italic">
                  {workspaceType === 'team'
                    ? 'No teammates added yet. Add people below so they can vote.'
                    : 'Only you (facilitator) so far. Add participants below.'}
                </div>
              )}
            </div>
          </div>

          {/* Zone 2 — Workspace members to add */}
          <div>
            <p className="text-xs text-[var(--text-ter)] mb-2">Add from your workspace</p>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)] pointer-events-none" />
              <input
                type="text"
                value={participantSearch}
                onChange={e => setParticipantSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full pl-8 pr-3 py-2 text-sm border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {usersLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 text-[var(--text-ter)] animate-spin" />
              </div>
            ) : filteredAvailableUsers.length === 0 ? (
              <p className="text-xs text-[var(--text-ter)] text-center py-4">
                {participantSearch ? 'No matches.' : 'All workspace members added.'}
              </p>
            ) : (
              <div className="max-h-52 overflow-y-auto border border-[var(--rule)] divide-y divide-[var(--rule)]">
                {filteredAvailableUsers.map(user => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => toggleParticipant(user.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--surface)] text-left transition-colors"
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-semibold shrink-0">
                      {getInitials(user.name ?? user.email)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--text-pri)] truncate">{user.name ?? user.email}</p>
                      {user.name && (
                        <p className="text-xs text-[var(--text-ter)] truncate">{user.email}</p>
                      )}
                    </div>
                    <PlusIcon className="h-4 w-4 text-primary-500 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Items step ───────────────────────────────────────────────────── */}
      {step === 'items' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[var(--text-ter)]">
              {availableItems.length} shaped item{availableItems.length !== 1 ? 's' : ''} available
            </span>
            <div className="flex gap-3">
              <button onClick={selectAll} className="text-xs text-primary-600 hover:underline">Select all</button>
              <button onClick={clearAll} className="text-xs text-[var(--text-ter)] hover:underline">Clear</button>
            </div>
          </div>

          {availableItems.length === 0 ? (
            <div className="border border-[var(--rule)] bg-[var(--surface)] px-4 py-8 text-center">
              <p className="text-sm text-[var(--text-ter)]">No items in your workspace yet.</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">You can add items inside the session once it's created.</p>
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto space-y-1 pr-1">
              {availableItems.map(item => (
                <label
                  key={item.id}
                  className="flex items-start gap-3 p-3 border border-[var(--rule)] cursor-pointer hover:bg-[var(--surface)] transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleItem(item.id)}
                    className="mt-0.5 rounded border-[var(--rule)] text-primary-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[var(--text-pri)] truncate">{item.name}</div>
                    {item.description && (
                      <div className="text-xs text-[var(--text-ter)] mt-0.5 line-clamp-1">{item.description}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            {selectedIds.size > 0 ? (
              <p className="text-xs text-primary-600 font-medium">
                {selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected
              </p>
            ) : <span />}

            {/* Pairwise ranking item-count warning */}
            {isPairwise && selectedIds.size > PAIRWISE_ITEM_WARN_THRESHOLD && (
              <div className="flex items-center gap-1.5 bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />
                <p className="text-xs text-[var(--mustard)]">
                  <span className="font-semibold">{selectedIds.size} items</span> selected — pairwise comparisons grow quadratically.
                  Consider keeping it under {PAIRWISE_ITEM_WARN_THRESHOLD} for a practical session.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tool config step ─────────────────────────────────────────────── */}
      {step === 'toolConfig' && configKind === 'dot_voting' && (
        <div className="space-y-5">
          <p className="text-sm text-[var(--text-sec)]">
            Set how many dots each participant gets to allocate across items.
          </p>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-3">
              Dots per participant
            </label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setDotsPerParticipant(v => Math.max(1, v - 1))}
                className="flex h-9 w-9 items-center justify-center border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors disabled:opacity-40"
                disabled={dotsPerParticipant <= 1}
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-12 text-center text-2xl font-bold text-[var(--text-pri)] tabular-nums">
                {dotsPerParticipant}
              </span>
              <button
                type="button"
                onClick={() => setDotsPerParticipant(v => Math.min(30, v + 1))}
                className="flex h-9 w-9 items-center justify-center border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors disabled:opacity-40"
                disabled={dotsPerParticipant >= 30}
              >
                <PlusIcon className="h-4 w-4" />
              </button>
              <span className="text-xs text-[var(--text-ter)] ml-1">max 30</span>
            </div>
            <p className="mt-3 text-xs text-[var(--text-ter)]">
              Typical range: 3–10 dots. More dots = more spread, fewer dots = sharper focus.
            </p>
          </div>
        </div>
      )}

      {step === 'toolConfig' && configKind === 'buy_a_feature' && (
        <div className="space-y-5">
          <p className="text-sm text-[var(--text-sec)]">
            Set what percentage of the total item value pool participants get as their budget.
          </p>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-3">
              Budget as % of total item value
            </label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setBudgetPercentage(v => Math.max(10, v - 10))}
                className="flex h-9 w-9 items-center justify-center border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors disabled:opacity-40"
                disabled={budgetPercentage <= 10}
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-16 text-center text-2xl font-bold text-[var(--text-pri)] tabular-nums">
                {budgetPercentage}%
              </span>
              <button
                type="button"
                onClick={() => setBudgetPercentage(v => Math.min(100, v + 10))}
                className="flex h-9 w-9 items-center justify-center border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors disabled:opacity-40"
                disabled={budgetPercentage >= 100}
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-3 text-xs text-[var(--text-ter)]">
              40% is the most common default. Lower forces harder trade-offs; higher lets participants buy nearly everything.
            </p>
          </div>
        </div>
      )}

      {step === 'toolConfig' && configKind === 'pareto_analysis' && (
        <div className="space-y-5">
          <p className="text-sm text-[var(--text-sec)]">
            Set where the vital few end: the leading items that together carry this share of the total value.
          </p>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-3">
              The vital few carry
            </label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setVitalFewShare(v => Math.max(50, v - 5))}
                className="flex h-9 w-9 items-center justify-center border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors disabled:opacity-40"
                disabled={vitalFewShare <= 50}
                aria-label="Less of the value"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-16 text-center text-2xl font-bold text-[var(--text-pri)] tabular-nums">
                {vitalFewShare}%
              </span>
              <button
                type="button"
                onClick={() => setVitalFewShare(v => Math.min(95, v + 5))}
                className="flex h-9 w-9 items-center justify-center border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors disabled:opacity-40"
                disabled={vitalFewShare >= 95}
                aria-label="More of the value"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-3 text-xs text-[var(--text-ter)]">
              80% is the classic 80/20 rule. Higher draws the line lower and keeps more items in the vital few.
            </p>
          </div>
        </div>
      )}

      {/* ── Criteria step ────────────────────────────────────────────────── */}
      {step === 'criteria' && (
        <div className="space-y-4">
          {toolDefinesCriteria && (
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] px-3 py-2.5 flex items-start gap-2">
              <SlidersHorizontal className="h-4 w-4 text-[var(--text-ter)] mt-0.5 shrink-0" />
              <p className="text-xs text-[var(--text-sec)]">
                <span className="font-semibold">{toolName}</span> has built-in evaluation dimensions.
                You can still add extra criteria below — they&apos;ll be available inside the session.
              </p>
            </div>
          )}
          {requiredCriteriaKeys.length > 0 && !toolDefinesCriteria && (
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] px-3 py-2.5 flex items-start gap-2">
              <SlidersHorizontal className="h-4 w-4 text-[var(--text-ter)] mt-0.5 shrink-0" />
              <p className="text-xs text-[var(--text-sec)]">
                <span className="font-semibold">{toolName}</span> requires these criteria:{' '}
                <span className="font-medium">{requiredCriteriaKeys.join(', ')}</span>.
                Matching workspace criteria have been pre-selected below.
              </p>
            </div>
          )}
          {!requiresCriteria && !toolDefinesCriteria && requiredCriteriaKeys.length === 0 && (
            <div className="bg-[var(--surface)] border border-[var(--rule)] px-3 py-2.5">
              <p className="text-xs text-[var(--text-ter)]">
                <span className="font-semibold">{toolName}</span> doesn&apos;t require criteria, but you can add optional scoring dimensions here if you want to track values against specific attributes.
              </p>
            </div>
          )}
          {criteriaLoading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading criteria…</span>
            </div>
          ) : (
            <>
              {/* Zone 1 — Your criteria (selected/configured, shown first) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--text-pri)]">
                    Criteria for this session
                  </span>
                  {selectedCriteriaIds.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedCriteriaIds(new Set())}
                      className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {selectedCriteriaIds.size === 0 ? (
                  <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)] px-4 py-4 text-center">
                    <p className="text-xs text-[var(--text-ter)]">
                      No criteria added yet. Create one below or pick from your workspace.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {workspaceCriteria
                      .filter(c => selectedCriteriaIds.has(c.id))
                      .map(criterion => (
                        <div
                          key={criterion.id}
                          className="flex items-center gap-2 px-3 py-2 border border-primary-200 bg-primary-50"
                        >
                          <span className="text-sm font-medium text-[var(--text-pri)] flex-1 truncate">
                            {criterion.label}
                          </span>
                          {criterion.weight !== undefined && (
                            <span className="text-xs text-[var(--text-ter)] flex-shrink-0">weight {criterion.weight}</span>
                          )}
                          <button
                            type="button"
                            onClick={() => toggleCriterion(criterion.id)}
                            className="p-0.5 text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
                            aria-label={`Remove ${criterion.label}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>

              {/* Zone 2 — Workspace criteria not yet selected (picker) */}
              {workspaceCriteria.filter(c => !selectedCriteriaIds.has(c.id)).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-[var(--text-sec)] mb-1.5">From your workspace</p>
                  <div className="max-h-36 overflow-y-auto space-y-1 pr-1">
                    {workspaceCriteria
                      .filter(c => !selectedCriteriaIds.has(c.id))
                      .map(criterion => (
                        <button
                          key={criterion.id}
                          type="button"
                          onClick={() => toggleCriterion(criterion.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 border border-[var(--rule)] bg-[var(--bg-raised)] hover:border-primary-300 hover:bg-primary-50 transition-colors text-left"
                        >
                          <PlusIcon className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                          <span className="text-sm text-[var(--text-sec)] flex-1 truncate">{criterion.label}</span>
                          {criterion.weight !== undefined && (
                            <span className="text-xs text-[var(--text-ter)] flex-shrink-0">weight {criterion.weight}</span>
                          )}
                        </button>
                      ))
                    }
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Value range step: the scale every criterion is scored on ─────── */}
      {step === 'criteriaRanges' && (
        <div className="space-y-4">
          {trackScale ? (
            <p className="text-sm text-[var(--text-sec)]" data-testid="wizard-track-range">
              Value range: {scoringScaleLabel(trackScale)}, from the track. Every criterion uses it, and everyone scoring sees it.
              {trackScaleLocked
                ? ' The track has locked it.'
                : ' Whoever runs the session can change it in its Values step until the track locks it.'}
            </p>
          ) : (
            <>
              <p className="text-xs text-[var(--text-ter)]">
                Choose the scale people score each item on. Every criterion uses it, and everyone scoring sees it.
              </p>
              <ScoringScaleChoice legend="Value range" value={scoringScale} onChange={setScoringScale} />
            </>
          )}
        </div>
      )}

      {/* ── Values step ──────────────────────────────────────────────────── */}
      {step === 'values' && (
        <div className="space-y-4">
          {(selectedCriteriaObjects.length === 0 || selectedIds.size === 0) ? (
            <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)] px-4 py-8 text-center">
              <Table2 className="h-8 w-8 text-[var(--text-ter)] mx-auto mb-2" />
              <p className="text-sm text-[var(--text-ter)]">
                {selectedCriteriaObjects.length === 0
                  ? 'No criteria selected — go back to pick criteria.'
                  : 'No items selected — go back to pick items.'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-[var(--text-ter)]">
                  Enter scores for each item against each criterion. Pre-filled from workspace where available.
                </p>
                {allValuesPresent && (
                  <span className="inline-flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)] border border-[var(--green)] rounded-full px-2.5 py-0.5">
                    <CheckCircle className="h-3 w-3" />
                    All values present
                  </span>
                )}
              </div>
              <div className="overflow-x-auto border border-[var(--rule)]">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--surface)] border-b border-[var(--rule)]">
                      <th className="px-3 py-2 text-left text-xs font-medium text-[var(--text-ter)] w-40">Item</th>
                      {selectedCriteriaObjects.map(c => (
                        <th key={c.id} className="px-3 py-2 text-center text-xs font-medium text-[var(--text-ter)] min-w-24">
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--rule)]">
                    {availableItems
                      .filter(item => selectedIds.has(item.id))
                      .map(item => (
                        <tr key={item.id} className="bg-[var(--bg-raised)] hover:bg-[var(--surface)]">
                          <td className="px-3 py-2 text-xs text-[var(--text-sec)] truncate max-w-[10rem]" title={item.name}>
                            {item.name}
                          </td>
                          {selectedCriteriaObjects.map(criterion => {
                            const existing = acceptedValues.find(
                              av => av.itemId === item.id && av.attributeKey === criterion.attributeKey && isOfficialValue(av)
                            );
                            const wizardVal = wizardItemValues[item.id]?.[criterion.attributeKey] ?? '';
                            const displayVal = existing
                              ? String(existing.value ?? '')
                              : wizardVal;
                            return (
                              <td key={criterion.id} className="px-2 py-1.5 text-center">
                                {existing ? (
                                  <div className="flex items-center justify-center gap-1">
                                    <span className="text-xs font-medium text-[var(--text-sec)]">{displayVal}</span>
                                    <Lock className="h-2.5 w-2.5 text-[var(--text-ter)]" title="Official value for the workspace" />
                                  </div>
                                ) : (
                                  <input
                                    type="number"
                                    value={wizardVal}
                                    onChange={e => setWizardItemValues(prev => ({
                                      ...prev,
                                      [item.id]: {
                                        ...(prev[item.id] ?? {}),
                                        [criterion.attributeKey]: e.target.value,
                                      },
                                    }))}
                                    placeholder="—"
                                    className="w-16 text-center px-1 py-0.5 text-xs border border-[var(--rule)] rounded focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400"
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-[var(--text-ter)]">
                Scores with <Lock className="h-3 w-3 inline text-[var(--text-ter)]" /> are the official values for your workspace.
                New scores are saved for this session only when the session is created, and offered up so someone who approves values can make them official.
              </p>
            </>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3">
          <p className="text-xs text-[var(--coral)]">{error}</p>
        </div>
      )}

      {/* Footer buttons */}
      <div className="flex items-center justify-between pt-2 border-t border-[var(--rule)]">
        <button
          type="button"
          onClick={currentIndex === 0 ? onCancel : goBack}
          className="inline-flex items-center gap-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
        >
          <ArrowLeft className="h-4 w-4" />
          {currentIndex === 0 ? 'Cancel' : 'Back'}
        </button>

        <button
          type="button"
          onClick={isLastStep ? handleSubmit : goNext}
          disabled={step === 'session' && !sessionName.trim()}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLastStep ? CREATE_SESSION_BUTTON_LABEL : 'Next'}
          {!isLastStep && <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
